import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { BreadCrumb } from "primereact/breadcrumb";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { HOME_ROUTE_PATH } from "../app_utils/route_paths/resolver/PageRoutes";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { PrimeIcons } from "primereact/api";

const ProductsView = () => {
  const [smsGateways, setSmsGateways] = useState<any[]>([]);
  const [emailGateways, setEmailGateways] = useState<any[]>([]);
  const [paymentGateways, setPaymentGateways] = useState<any[]>([]);
  const [dataTelcos, setDataTelcos] = useState<any[]>([]);
  const [bankTransferGateways, setBankTransferGateways] = useState<any[]>([]);
  const [mobileMoneyGateways, setMobileMoneyGateways] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const breadcrumbHome = {
    icon: "pi pi-home",
    command: () => {
      window.location.href = HOME_ROUTE_PATH;
    },
  };

  useEffect(() => {
    setIsLoading(true);
    const api = new BaseApiServiceImpl("/api/v1/gateways");
    api
      .getRequestWithJsonResponse({ offset: 0, limit: 100 })
      .then((response) => {
        const raw = response?.records || response?.data || response || [];
        setSmsGateways(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setSmsGateways([]));

    const emailApi = new BaseApiServiceImpl("/api/v1/email-gateways");
    emailApi
      .getRequestWithJsonResponse({ offset: 0, limit: 100 })
      .then((response) => {
        const raw = response?.records || response?.data || response || [];
        setEmailGateways(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setEmailGateways([]));

    const paymentApi = new BaseApiServiceImpl("/api/v1/payment-gateways");
    paymentApi
      .getRequestWithJsonResponse({ offset: 0, limit: 100 })
      .then((response) => {
        const raw = response?.records || response?.data || response || [];
        const all = Array.isArray(raw) ? raw : [];
        setPaymentGateways(all);
        setBankTransferGateways(all.filter((g: any) => g.productType === "BANK_TRANSFER"));
        setMobileMoneyGateways(all.filter((g: any) => g.productType === "MOBILE_MONEY_DISBURSEMENT"));
      })
      .catch(() => {
        setPaymentGateways([]);
        setBankTransferGateways([]);
        setMobileMoneyGateways([]);
      })
      .finally(() => setIsLoading(false));

    new BaseApiServiceImpl("/api/v1/data-bundles/catalogue")
      .getRequestWithJsonResponse({})
      .then((response) => {
        setDataTelcos(response?.data?.telcos || []);
      })
      .catch(() => setDataTelcos([]));
  }, []);

  const getStatusSeverity = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "deactivated":
        return "danger";
      case "pending":
        return "warning";
      default:
        return "info";
    }
  };

  const getGatewaySubtitle = (item: any, type: string) => {
    if (type === "sms") {
      return item.baseUrl || item.apiUsername || "-";
    }
    if (type === "email") {
      return item.host || item.senderEmail || "-";
    }
    if (type === "payment") {
      return item.baseUrl || item.providerName || "-";
    }
    if (type === "data") {
      return `${item.bundles?.length ?? 0} bundles available`;
    }
    return "-";
  };

  const getGatewayName = (item: any, type: string) => {
    if (type === "data") {
      return item.telco || "Unnamed";
    }
    return item.name || item.providerName || item.knownAggregatorName || "Unnamed";
  };

  const ProductCard = ({ title, icon, iconColor, items, type }: any) => (
    <div className="col-12 md:col-4">
      <Card className="h-full">
        <div className="flex align-items-center mb-3">
          <i className={icon} style={{ fontSize: "2rem", color: iconColor, marginRight: "0.75rem" }}></i>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600 }}>{title}</h3>
            <small style={{ color: "#64748b" }}>
              {type === "sms"
                ? "Short message service gateways"
                : type === "email"
                ? "Transactional and bulk email gateways"
                : type === "data"
                ? "Mobile data bundle aggregation"
                : "Mobile money and payment collectors"}
            </small>
          </div>
        </div>
        <div className="mt-3">
          {items.length === 0 ? (
            <p style={{ color: "#94a3b8", fontSize: "0.85rem", fontStyle: "italic" }}>
              No {title.toLowerCase()} gateways configured
            </p>
          ) : (
            <div className="flex flex-column gap-2">
              {items.map((item: any, idx: number) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0.5rem 0.75rem",
                    background: "#f8fafc",
                    borderRadius: "0.375rem",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500, fontSize: "0.9rem" }}>{getGatewayName(item, type)}</div>
                    <small style={{ color: "#64748b", fontSize: "0.75rem" }}>
                      {getGatewaySubtitle(item, type)}
                    </small>
                  </div>
                  <Tag
                    value={type === "data" ? "Active" : item.status || "Deactivated"}
                    severity={type === "data" ? "success" : getStatusSeverity(item.status)}
                    style={{ fontSize: "0.75rem" }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <Card>
          <BreadCrumb home={breadcrumbHome} model={[{ label: "Products" }]} />
          <h2 style={{ margin: "1rem 0 0.25rem" }}>Products</h2>
          <p style={{ color: "#64748b", marginBottom: "1.5rem" }}>
            Overview of communication services configured on this platform.
          </p>

          {isLoading ? (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
              <ProgressSpinner style={{ width: "50px", height: "50px" }} />
            </div>
          ) : (
            <div className="grid">
              <ProductCard
                title="SMS"
                icon={PrimeIcons.COMMENT}
                iconColor="#3b82f6"
                type="sms"
                items={smsGateways}
              />
              <ProductCard
                title="Email"
                icon={PrimeIcons.ENVELOPE}
                iconColor="#10b981"
                type="email"
                items={emailGateways}
              />
              <ProductCard
                title="Payments"
                icon={PrimeIcons.WALLET}
                iconColor="#f59e0b"
                type="payment"
                items={paymentGateways}
              />
              <ProductCard
                title="Bank Transfers"
                icon="pi pi-credit-card"
                iconColor="#1565c0"
                type="payment"
                items={bankTransferGateways}
              />
              <ProductCard
                title="Mobile Money Disbursement"
                icon={PrimeIcons.MOBILE}
                iconColor="#2e7d32"
                type="payment"
                items={mobileMoneyGateways}
              />
              <ProductCard
                title="Data Bundles"
                icon={PrimeIcons.TABLET}
                iconColor="#8b5cf6"
                type="data"
                items={dataTelcos}
              />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ProductsView;
